import { Grid, IconButton, InputAdornment, Tooltip } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { PaymentMethodEnum } from 'src/shared/models/enums/paymentMethod.enum';
import { PersonAdd, PersonRemove } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { useGlobals } from 'src/shared/contexts/appContext';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { CurrencyEnum } from 'src/shared/models/enums/currency.enum';
import { AssociationFeeRequest } from '../models/associationFeeRequest';

type SendingMethodHandlerProps = {
  paymentMethod: PaymentMethodEnum;
  readOnlyMode: boolean;
};

export const AssociationSendingMethodHandler = (props: SendingMethodHandlerProps) => {
  const { paymentMethod, readOnlyMode } = props;
  const { t } = useTranslation();
  const { currentUser } = useGlobals();
  const currency = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.Currency, CurrencyEnum.SEK);

  const { control } = useFormContext<AssociationFeeRequest>();
  const { fields, append, remove } = useFieldArray<AssociationFeeRequest, 'familySteps', 'id'>({
    control,
    name: 'familySteps',
  });

  return (
    <>
      {paymentMethod === PaymentMethodEnum.SameFeeForAll && (
        <Grid item xs={12} md={5}>
          <ControlledTextField
            disabled={readOnlyMode}
            label={t('association:paymentMethodFieldLabels.sameFeeForAll')}
            type='number'
            name='sameFeeForAll'
            InputProps={{
              endAdornment: (
                <>
                  <InputAdornment position='end'>{currency}</InputAdornment>
                </>
              ),
            }}
          />
        </Grid>
      )}

      {paymentMethod === PaymentMethodEnum.MainAndFamily && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <ControlledTextField
              disabled={readOnlyMode}
              label={t('association:paymentMethodFieldLabels.feeMainMember')}
              type='number'
              name='mainAndFamily.feeMainMember'
              InputProps={{
                endAdornment: (
                  <>
                    <InputAdornment position='end'>{currency}</InputAdornment>
                  </>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <ControlledTextField
              disabled={readOnlyMode}
              label={t('association:paymentMethodFieldLabels.feeFamilyMember')}
              type='number'
              name='mainAndFamily.feeFamilyMember'
              InputProps={{
                endAdornment: (
                  <>
                    <InputAdornment position='end'>{currency}</InputAdornment>
                  </>
                ),
              }}
            />
          </Grid>
        </Grid>
      )}

      {paymentMethod === PaymentMethodEnum.FamilySteps && (
        <Grid container>
          <Grid item xs={12} md={7}>
            {fields.map((field, index) => {
              const getLabel = () => {
                if (field.member === 'mainMember') return t('association:paymentMethodFieldLabels.familyStepMainMember');
                return t('association:paymentMethodFieldLabels.familyStepfamilyMember', { count: index });
              };
              const isFixed = field.member === 'mainMember' || field.member === 'familyMember1';
              const isMainMember = field.member === 'mainMember';
              const isLast = index === fields.length - 1;

              return (
                <Grid container mb={2} key={field.id}>
                  <Grid item xs={10}>
                    <ControlledTextField
                      disabled={readOnlyMode}
                      label={getLabel()}
                      type='number'
                      name={`familySteps.${index}.fee`}
                      InputProps={{
                        endAdornment: (
                          <>
                            <InputAdornment position='end'>{currency}</InputAdornment>
                          </>
                        ),
                      }}
                    />
                  </Grid>
                  {isMainMember && (
                    <Grid item xs={1} ml={1} sx={{ display: 'flex', alignItems: 'center' }}>
                      {!readOnlyMode && (
                        <Tooltip title={t('association:paymentMethodFieldLabels.addFamilyMember')} placement='right-end'>
                          <IconButton
                            onClick={() => {
                              const currentCount = fields.filter((f) => f.member?.startsWith('familyMember')).length;
                              const newLabel = `familyMember${currentCount + 1}`;
                              append({ member: newLabel, fee: undefined });
                            }}
                          >
                            <PersonAdd color='primary' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Grid>
                  )}
                  {!isFixed && isLast && (
                    <Grid item xs={1} ml={1} sx={{ display: 'flex', alignItems: 'center' }}>
                      {!readOnlyMode && (
                        <Tooltip title={t('association:paymentMethodFieldLabels.removeFamilyMember')} placement='right-end'>
                          <IconButton color='error' onClick={() => remove(index)}>
                            <PersonRemove fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Grid>
                  )}
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      )}

      {paymentMethod === PaymentMethodEnum.OneFeeForHousehold && (
        <Grid item xs={12} md={5}>
          <ControlledTextField
            disabled={readOnlyMode}
            label={t('association:paymentMethodFieldLabels.oneFeeForHousehold')}
            type='number'
            name='oneFeeForHousehold'
            InputProps={{
              endAdornment: (
                <>
                  <InputAdornment position='end'>{currency}</InputAdornment>
                </>
              ),
            }}
          />
        </Grid>
      )}
    </>
  );
};
